<template>
  <div class="d-flex flex-wrap align-items-center" style="gap: 8px;" v-if="canHealth || canExecutive">
    <!-- Health check: runs now, the result arrives as an in-app message -->
    <div v-if="canHealth" class="btn-group btn-group-sm ra-menu-wrap">
      <button class="btn btn-outline-danger" :disabled="healthBusy" @click="healthMenu = !healthMenu" title="Run the data health check; the result comes as an in-app message">
        <span v-if="healthBusy" class="spinner-border spinner-border-sm mr-1"></span><i v-else class="fe fe-heart mr-1"></i>
        {{ healthBusy ? (healthMode === "now" ? "Checking…" : "Starting…") : "Run health check" }} <i class="fe fe-chevron-down ml-1"></i>
      </button>
      <router-link to="/system-health" class="btn btn-outline-danger" title="Open the System Health page"><i class="fe fe-external-link"></i></router-link>
      <div v-if="healthMenu" class="ra-menu" @mouseleave="healthMenu = false">
        <button class="ra-option" @click="runHealth('now')">
          <b><i class="fe fe-zap mr-1"></i> Run now</b>
          <small>Wait here for the result (can take a minute or two). Also sent as an in-app message.</small>
        </button>
        <button class="ra-option" @click="runHealth('queue')">
          <b><i class="fe fe-clock mr-1"></i> Run in the background</b>
          <small>Keep working; the result arrives as an in-app message when it is ready.</small>
        </button>
      </div>
    </div>

    <!-- Executive summary: sent by email and in-app message -->
    <div v-if="canExecutive" class="btn-group btn-group-sm">
      <button class="btn btn-outline-primary" @click="openExecutive" title="Send the executive summary by email and in-app message">
        <i class="fe fe-send mr-1"></i> Send executive summary
      </button>
      <router-link to="/executive-summary" class="btn btn-outline-primary" title="Open the Executive Summary page"><i class="fe fe-external-link"></i></router-link>
    </div>

    <div v-if="execOpen" class="ra-backdrop" @click.self="execOpen = false">
      <div class="ra-dialog" role="dialog" aria-modal="true" aria-labelledby="ra-title">
        <div class="ra-head">
          <div>
            <h5 id="ra-title" class="mb-0">Send executive summary</h5>
            <small class="text-muted">Emailed and sent as an in-app message to the Executive Secretary, the Directors and you.</small>
          </div>
          <button class="btn btn-sm btn-link text-secondary" @click="execOpen = false" aria-label="Close"><i class="fe fe-x"></i></button>
        </div>
        <div class="ra-body">
          <label class="small font-weight-bold text-secondary">Period</label>
          <select class="form-control form-control-sm mb-2" v-model="preset" @change="applyPreset">
            <option value="last_week">Last week (Monday–Sunday)</option>
            <option value="this_week">This week so far</option>
            <option value="last_30">Last 30 days</option>
            <option value="last_month">Last month</option>
            <option value="custom">Custom dates</option>
          </select>
          <div class="form-row">
            <div class="col"><label class="small text-muted mb-1">From</label><input type="date" class="form-control form-control-sm" v-model="from" @change="preset = 'custom'" /></div>
            <div class="col"><label class="small text-muted mb-1">To</label><input type="date" class="form-control form-control-sm" v-model="to" @change="preset = 'custom'" /></div>
          </div>
        </div>
        <div class="ra-foot">
          <router-link :to="`/executive-summary?from=${from}&to=${to}`" class="btn btn-sm btn-link mr-auto">Preview</router-link>
          <button class="btn btn-sm btn-outline-secondary" @click="execOpen = false" :disabled="execBusy">Cancel</button>
          <button class="btn btn-sm btn-primary" :disabled="execBusy || !from || !to" @click="sendExecutive">
            <span v-if="execBusy" class="spinner-border spinner-border-sm mr-1"></span><i v-else class="fe fe-send mr-1"></i> Send
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

/**
 * State Dashboard buttons: "Run health check" (POST health/run, result as an in-app message to the admins and the
 * person who pressed it; agency admins) and "Send executive summary" (POST reports/executive-summary/send, by email
 * and in-app message to the Executive Secretary, Directors and the sender; admins, Executive Secretary, Directors).
 */
export default {
  name: "ReportActions",
  data() {
    return { user: JSON.parse(localStorage.getItem("user") || "{}") || {}, healthBusy: false, healthMenu: false, healthMode: "now", execOpen: false, execBusy: false, preset: "last_week", from: "", to: "" };
  },
  computed: {
    isAdmin() { return this.user.type === "shis" || Number(this.user.user_role) === 1; },
    canHealth() { return this.isAdmin; },
    canExecutive() {
      const t = String(this.user.job_title || "").trim().toLowerCase();
      return this.isAdmin || t === "executive secretary" || t === "director";
    },
  },
  created() { this.applyPreset(); },
  methods: {
    toast(msg, ok = true) { this.$toasted[ok ? "success" : "error"](msg, { position: "top-center", duration: 6000 }); },
    error(e, fallback) { return (e.response && e.response.data && e.response.data.message) || fallback; },
    // mode "now": runs during the request and shows the result; "queue": runs on the queue and messages when done
    runHealth(mode) {
      this.healthMenu = false;
      this.healthMode = mode;
      this.healthBusy = true;
      this.axios.post("/api/v1/auth/health/run", { send: 1, channels: ["chat"], mode }, { timeout: 0 })
        .then((res) => {
          if (mode === "now") {
            this.$toasted.success(res.data.message, {
              position: "top-center", duration: 10000,
              action: { text: "View", onClick: (e, t) => { t.goAway(0); this.$router.push("/system-health"); } },
            });
          } else {
            this.toast(res.data.message || "Health check started. The result will arrive as an in-app message.");
          }
        })
        .catch((e) => this.toast(this.error(e, "Could not run the health check."), false))
        .finally(() => { this.healthBusy = false; });
    },
    openExecutive() { this.applyPreset(); this.execOpen = true; },
    applyPreset() {
      const set = (a, b) => { this.from = a.format("YYYY-MM-DD"); this.to = b.format("YYYY-MM-DD"); };
      const m = moment();
      switch (this.preset) {
        case "this_week": set(m.clone().isoWeekday(1), m.clone()); break;
        case "last_30": set(m.clone().subtract(29, "days"), m.clone()); break;
        case "last_month": set(m.clone().subtract(1, "month").startOf("month"), m.clone().subtract(1, "month").endOf("month")); break;
        case "last_week": set(m.clone().isoWeekday(1).subtract(7, "days"), m.clone().isoWeekday(1).subtract(1, "days")); break;
      }
    },
    sendExecutive() {
      this.execBusy = true;
      this.axios.post("/api/v1/auth/reports/executive-summary/send", { from: this.from, to: this.to })
        .then((res) => { this.toast(res.data.message); this.execOpen = false; })
        .catch((e) => this.toast(this.error(e, "Could not send the executive summary."), false))
        .finally(() => { this.execBusy = false; });
    },
  },
};
</script>

<style scoped>
.ra-menu-wrap { position: relative; }
.ra-menu { position: absolute; top: 100%; left: 0; z-index: 1050; margin-top: 4px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; box-shadow: 0 10px 30px rgba(15, 23, 42, 0.15); width: 300px; padding: 6px; }
.ra-option { display: block; width: 100%; text-align: left; background: none; border: 0; border-radius: 8px; padding: 8px 10px; color: #1e293b; }
.ra-option:hover { background: #f1f5f9; }
.ra-option small { display: block; color: #64748b; font-size: 0.75rem; margin-top: 2px; }
.ra-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); z-index: 1060; display: flex; align-items: flex-start; justify-content: center; padding: 64px 16px; }
.ra-dialog { background: #fff; border-radius: 12px; width: 100%; max-width: 460px; box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25); }
.ra-head { display: flex; justify-content: space-between; align-items: flex-start; padding: 16px 20px 12px; border-bottom: 1px solid #e2e8f0; }
.ra-body { padding: 16px 20px; }
.ra-foot { display: flex; align-items: center; gap: 8px; padding: 12px 20px; border-top: 1px solid #e2e8f0; }
</style>
